import { RefObject, useState } from 'react'
import { GenerativeAIContent } from 'shared'
import { useEventSource } from './useEventSource'

interface UseAIChatProps {
  projectId: string
  fieldHint: string
  scrollRef?: RefObject<HTMLDivElement>
}

export const useAIChat = ({ projectId, fieldHint, scrollRef }: UseAIChatProps) => {
  const [history, setHistory] = useState<GenerativeAIContent[]>([])
  const [sentMessage, setSentMessage] = useState('')

  const { isLoading, message, connect, getMessageChunks } = useEventSource({
    url: '/ai/rich-text',
    onClose: (payload) => {
      setHistory((prev) => [
        ...prev,
        createUserContent(payload.parts[0].text ?? ""),
        createModelContent(getMessageChunks()),
      ])
      setSentMessage('')
      scrollRef?.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      })
    },
  })

  const createUserContent = (text: string, files: any[] = []): GenerativeAIContent => ({
    parts: [
      { text },
      ...files.map((file) => ({
        fileData: {
          mimeType: file.file.mimeType,
          fileUri: file.file.uri,
        },
      })),
    ],
    role: 'user',
  })

  const createModelContent = (text: string): GenerativeAIContent => ({
    role: 'model',
    parts: [{ text }],
  })

  const sendMessage = async (text: string, files: any[] = []) => {
    setSentMessage(text)
    await connect({
      parts: createUserContent(text, files).parts,
      history,
      projectId,
      fieldHint,
    })
  }

  return {
    history,
    isLoading,
    message,
    sentMessage,
    sendMessage,
  }
}
